import type { Task, TagRef } from './types';

/**
 * Applies a task update to client state before the server response arrives.
 *
 * `tag_names` is intentionally a write-only wire field: server responses use
 * `tags: [{ id, name }]`. Translate it here so optimistic UI (and a fast
 * close/reopen of TaskDetailModal) shows the newly selected tag names instead
 * of retaining stale chips. Existing tag IDs are preserved; newly created
 * tags receive a temporary ID until the server response replaces the row.
 */
export function applyTaskOptimisticUpdate(task: Task, updates: Partial<Task>): Task {
  const { tag_names: tagNames, ...otherUpdates } = updates;

  if (!tagNames) return { ...task, ...otherUpdates };

  const existingTags = new Map(
    (task.tags ?? []).map((tag) => [tag.name.trim().toLowerCase(), tag]),
  );
  const tags: TagRef[] = tagNames.map((name) => {
    const normalized = name.trim().toLowerCase();
    return existingTags.get(normalized) ?? { id: `pending:${normalized}`, name };
  });

  return { ...task, ...otherUpdates, tags, tag_names: undefined };
}
